import { useState, useEffect, useRef, useCallback } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { AppShell } from '../../components/AppShell'
import {
  ordersAtom,
  newOrdersAtom,
  todayStatsAtom,
  loadingAtom,
  errorAtom,
  fetchedAtom,
  fetchOrdersAtom,
  markAllSeenAtom,
  useOrdersPolling,
  type AmazonOrder,
  type TodayStats,
} from '../../stores/AmazonOrdersStore'

// ============================================================================
// Helpers
// ============================================================================

function formatCurrency(amount: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount)
}

function timeAgo(dateStr: string): string {
  const now = Date.now()
  const then = new Date(dateStr).getTime()
  const diffMs = now - then
  const minutes = Math.floor(diffMs / 60000)
  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

// ============================================================================
// Stat Card
// ============================================================================

function StatCard({
  label,
  value,
  subtitle,
  icon,
  accentColor = '#18181B',
  pulseClass = '',
}: {
  label: string
  value: string
  subtitle?: string
  icon: React.ReactNode
  accentColor?: string
  pulseClass?: string
}) {
  const [tickClass, setTickClass] = useState('')
  const prevValueRef = useRef(value)

  useEffect(() => {
    if (prevValueRef.current !== value && prevValueRef.current !== '') {
      setTickClass('animate-counter-tick')
      const timer = setTimeout(() => setTickClass(''), 400)
      prevValueRef.current = value
      return () => clearTimeout(timer)
    }
    prevValueRef.current = value
  }, [value])

  return (
    <div className={`bg-white border border-[#E5E5E5] rounded-xl p-4 ${pulseClass}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-[12px] font-medium text-[#71717A] uppercase tracking-wider">{label}</span>
        <div className="size-8 rounded-lg bg-[#F5F5F5] flex items-center justify-center">
          {icon}
        </div>
      </div>
      <div className={tickClass}>
        <p className="text-2xl font-bold" style={{ color: accentColor }}>{value}</p>
      </div>
      {subtitle && <p className="text-[11px] text-[#A1A1AA] mt-1">{subtitle}</p>}
    </div>
  )
}

// ============================================================================
// Order Card
// ============================================================================

function OrderStatusBadge({ status }: { status: string }) {
  const s = status.toLowerCase()
  if (s === 'shipped') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#DCFCE7] text-[#166534]">
        <span className="size-1.5 rounded-full bg-[#22C55E]" />
        Shipped
      </span>
    )
  }
  if (s === 'unshipped' || s === 'pending') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#FEF9C3] text-[#854D0E]">
        <span className="size-1.5 rounded-full bg-[#EAB308]" />
        {s === 'pending' ? 'Pending' : 'Unshipped'}
      </span>
    )
  }
  if (s === 'canceled') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#FEF2F2] text-[#DC2626]">
        <span className="size-1.5 rounded-full bg-[#EF4444]" />
        Canceled
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#F5F5F5] text-[#52525B]">
      <span className="size-1.5 rounded-full bg-[#A1A1AA]" />
      {status}
    </span>
  )
}

function OrderCard({ order, isNew }: { order: AmazonOrder; isNew: boolean }) {
  const amount = order.OrderTotal?.Amount ? parseFloat(order.OrderTotal.Amount) : 0
  const currency = order.OrderTotal?.CurrencyCode || 'USD'
  const location = [order.ShippingAddress?.City, order.ShippingAddress?.StateOrRegion]
    .filter(Boolean)
    .join(', ')
  const isFBA = order.FulfillmentChannel === 'AFN'
  const totalItems = order.NumberOfItemsShipped + order.NumberOfItemsUnshipped

  return (
    <div className={`relative bg-white border border-[#E5E5E5] rounded-xl p-4 ${isNew ? 'animate-order-slide-in' : ''}`}>
      {/* Coin animation overlay for new orders */}
      {isNew && (
        <div className="absolute -top-2 right-4 pointer-events-none">
          <span className="inline-block text-lg animate-coin-float" style={{ animationDelay: '0ms' }}>🪙</span>
          <span className="inline-block text-lg animate-coin-bounce" style={{ animationDelay: '150ms' }}>💰</span>
          <span className="inline-block text-sm animate-coin-float" style={{ animationDelay: '300ms' }}>🪙</span>
        </div>
      )}

      {/* Money float animation */}
      {isNew && amount > 0 && (
        <div className="absolute -top-1 left-1/2 -translate-x-1/2 pointer-events-none">
          <span className="text-[13px] font-bold text-[#16A34A] animate-money-float">
            +{formatCurrency(amount, currency)}
          </span>
        </div>
      )}

      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-[13px] font-semibold text-[#18181B] font-mono">
              {order.AmazonOrderId}
            </span>
            <OrderStatusBadge status={order.OrderStatus} />
          </div>
          <div className="flex items-center gap-3 text-[12px] text-[#71717A]">
            <span>{timeAgo(order.PurchaseDate)}</span>
            {totalItems > 0 && (
              <span>{totalItems} item{totalItems !== 1 ? 's' : ''}</span>
            )}
            {location && <span>{location}</span>}
            {isFBA && (
              <span className="px-1.5 py-0.5 rounded bg-[#EFF6FF] text-[#1D4ED8] text-[10px] font-medium">FBA</span>
            )}
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className="text-[15px] font-bold text-[#18181B]">
            {amount > 0 ? formatCurrency(amount, currency) : '—'}
          </p>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Page Component
// ============================================================================

export default function OrdersDashboard() {
  const orders = useAtomValue(ordersAtom)
  const newOrders = useAtomValue(newOrdersAtom)
  const stats = useAtomValue(todayStatsAtom)
  const loading = useAtomValue(loadingAtom)
  const error = useAtomValue(errorAtom)
  const fetched = useAtomValue(fetchedAtom)
  const fetchOrders = useSetAtom(fetchOrdersAtom)
  const markAllSeen = useSetAtom(markAllSeenAtom)

  // Start polling
  useOrdersPolling(30_000)

  // Track new order IDs for animation
  const [animatingIds, setAnimatingIds] = useState<Set<string>>(new Set())
  const [revenuePulse, setRevenuePulse] = useState(false)

  // When new orders arrive, trigger animations then mark as seen
  useEffect(() => {
    if (newOrders.length === 0) return

    const newIds = new Set(newOrders.map((o) => o.AmazonOrderId))
    setAnimatingIds(newIds)
    setRevenuePulse(true)

    // Clear animations and mark seen after animation completes
    const timer = setTimeout(() => {
      setAnimatingIds(new Set())
      setRevenuePulse(false)
      markAllSeen()
    }, 2000)

    return () => clearTimeout(timer)
  }, [newOrders, markAllSeen])

  // Sort orders by PurchaseDate descending (most recent first)
  const sortedOrders = [...orders].sort(
    (a, b) => new Date(b.PurchaseDate).getTime() - new Date(a.PurchaseDate).getTime(),
  )

  return (
    <AppShell activeNav="orders">
      <div className="flex-1 overflow-y-auto">
        <div className="px-6 py-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="flex items-center gap-2.5">
                <h1 className="text-lg font-semibold text-[#18181B]">Orders Dashboard</h1>
                <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#F0FDF4]">
                  <span className="size-2 rounded-full bg-[#16A34A] animate-live-pulse" />
                  <span className="text-[11px] font-semibold text-[#16A34A]">Live</span>
                </div>
              </div>
              <p className="text-[13px] text-[#71717A] mt-1">Real-time orders from your Amazon store</p>
            </div>
            <div className="flex items-center gap-3">
              {/* Market badge */}
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#E5E5E5] bg-white">
                <span className="text-base leading-none">🇺🇸</span>
                <span className="text-[12px] font-medium text-[#18181B]">US Market</span>
              </div>
              <button
                onClick={() => fetchOrders()}
                disabled={loading}
                className="px-3 h-9 bg-[#18181B] text-white rounded-lg text-[12px] font-medium flex items-center gap-2 hover:bg-[#3F3F46] transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className={`size-3.5 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                </svg>
                {loading ? 'Fetching...' : 'Refresh'}
              </button>
            </div>
          </div>

          {/* Error state */}
          {error && !loading && (
            <div className="mb-6 flex items-center gap-3 p-3 rounded-lg bg-[#FEF2F2] border border-[#FECACA]">
              <svg className="size-5 text-[#EF4444] shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
              </svg>
              <p className="text-[13px] text-[#DC2626]">{error}</p>
            </div>
          )}

          {/* Stats cards */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <StatCard
              label="Orders Today"
              value={stats.ordersToday.toString()}
              subtitle="Total orders"
              accentColor="#18181B"
              icon={
                <svg className="size-4 text-[#71717A]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 1 0-7.5 0v4.5m11.356-1.993 1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 0 1-1.12-1.243l1.264-12A1.125 1.125 0 0 1 5.513 7.5h12.974c.576 0 1.059.435 1.119 1.007ZM8.625 10.5a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm7.5 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
                </svg>
              }
            />
            <StatCard
              label="Revenue"
              value={formatCurrency(stats.revenueToday, stats.currencyCode)}
              subtitle="Today's total"
              accentColor="#16A34A"
              pulseClass={revenuePulse ? 'animate-revenue-pulse' : ''}
              icon={
                <svg className="size-4 text-[#16A34A]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                </svg>
              }
            />
            <StatCard
              label="Pending"
              value={stats.pendingCount.toString()}
              subtitle="Awaiting shipment"
              accentColor="#D97706"
              icon={
                <svg className="size-4 text-[#D97706]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                </svg>
              }
            />
            <StatCard
              label="Shipped"
              value={stats.shippedCount.toString()}
              subtitle="Fulfilled orders"
              accentColor="#2563EB"
              icon={
                <svg className="size-4 text-[#2563EB]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 0 1-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 0 0-3.213-9.193 2.056 2.056 0 0 0-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 0 0-10.026 0 1.106 1.106 0 0 0-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
                </svg>
              }
            />
          </div>

          {/* Initial loading state */}
          {loading && !fetched && (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="size-12 rounded-full bg-[#FFFBEB] flex items-center justify-center mb-4">
                <svg className="size-6 text-[#F59E0B] animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                </svg>
              </div>
              <h2 className="text-[15px] font-semibold text-[#18181B] mb-1">Connecting to Amazon Orders...</h2>
              <p className="text-[13px] text-[#71717A] text-center max-w-sm">
                Fetching your latest orders. This may take a few seconds.
              </p>
            </div>
          )}

          {/* Empty state — no orders today */}
          {!loading && fetched && orders.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="size-14 rounded-full bg-[#F5F5F5] flex items-center justify-center mb-4">
                <span className="text-2xl">📦</span>
              </div>
              <h2 className="text-[15px] font-semibold text-[#18181B] mb-1">No orders yet today</h2>
              <p className="text-[13px] text-[#71717A] text-center max-w-sm">
                Orders will appear here in real-time as they come in. Leave this page open to watch the magic!
              </p>
            </div>
          )}

          {/* Orders feed */}
          {fetched && orders.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-[13px] font-semibold text-[#18181B]">Recent Orders</h2>
                <span className="text-[12px] text-[#A1A1AA]">
                  {loading && <span className="animate-pulse">Refreshing...</span>}
                  {!loading && `${orders.length} order${orders.length !== 1 ? 's' : ''} today`}
                </span>
              </div>
              <div className="space-y-2.5">
                {sortedOrders.map((order) => (
                  <OrderCard
                    key={order.AmazonOrderId}
                    order={order}
                    isNew={animatingIds.has(order.AmazonOrderId)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </AppShell>
  )
}
